import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as models from '@owl-nest/models'

import { t } from '@owl-nest/localize'
import { MoneyDisplayer } from '@owl-nest/money'
import * as plume from '@ulule/owl-kit-components/next'

import { RootState, AppState } from '../../../../../../reducers'

import { useIndice } from '../../../hooks/useIndice'
import { AccordionWrapper } from '../../../components/AccordionWrapper'

import * as Styles from '../../../style'

type EngineTagProps = {
  children: React.ReactElement
  type: api.SponsorshipType
}

function EngineTag({ type, children }: EngineTagProps): React.ReactElement<EngineTagProps> {
  const tagsTypes = {
    amount: {
      icon: '⚡️',
      text: '+ ',
    },
    prize_winner: {
      icon: '🏆',
      text: t('Winner'),
    },
    doubled_donations: {
      icon: '💰',
      text: t('Matched donations'),
    },
    crush: {
      icon: '❤️',
      text: t('Heartthrob'),
    },
    audience: {
      icon: '👏',
      text: t('Crowd favorite prize'),
    },
  }

  return (
    <>
      <span role="img" aria-label="price emoji">
        {tagsTypes[type].icon}
      </span>
      {tagsTypes[type].text}
      {type === 'amount' && children}
    </>
  )
}

export function Sponsors(): React.ReactElement<unknown> {
  const { isOpen, updateOpenIndices } = useIndice()
  const { project: projectState } = Redux.useSelector<RootState, AppState>((state) => state.application)
  const project = projectState.data
  const { sponsorships } = project
  const isMobile = !window.matchMedia(plume.BREAKPOINTS.LAPTOP).matches
  const Heading = isMobile ? plume.styles.heading.S : plume.styles.heading.M

  sponsorships?.sort(function (sponsorA, sponsorB) {
    if (sponsorA.mode !== 'prize_winner' && sponsorB.mode == 'prize_winner') {
      return 1
    } else if (sponsorA.mode == 'prize_winner' && sponsorB.mode !== 'prize_winner') {
      return -1
    }
    return 0
  })

  return (
    <>
      {sponsorships && sponsorships.length > 0 && (
        <Styles.SidebarItem>
          <SponsorHeading>
            <Heading>{t("Project's partner")}</Heading>
            <plume.styles.copy.M.light>
              {t("This project benefits from the support of Ulule's partners:")}
            </plume.styles.copy.M.light>
          </SponsorHeading>
          {sponsorships.map((sponsorshipData, i) => {
            // We force cast the sponsorship to Sponsorship<'channel'> because
            // the sponsorship should inherits of the 'sponsorships.channel' value
            const sponsorship = sponsorshipData as unknown as api.Sponsorship<'channel'>
            return (
              <SponsorContainer key={i}>
                <SponsorMode>{models.sponsorship.mode(sponsorship)}</SponsorMode>
                <HeaderContainer
                  onClick={() => {
                    updateOpenIndices(i.toString())
                  }}
                >
                  <Avatar>
                    <plume.styles.image.Avatar src={sponsorship.sponsor.image.versions?.small.url} type="small" />
                  </Avatar>
                  <HeaderInfo>
                    <TitleContainer>
                      <plume.styles.heading.XS>{sponsorship.sponsor.name}</plume.styles.heading.XS>
                    </TitleContainer>
                    {sponsorship.type && (
                      <plume.Tag color="sand">
                        <EngineTag type={sponsorship.type}>
                          <MoneyDisplayer amount={sponsorship.amount} project={project} />
                        </EngineTag>
                      </plume.Tag>
                    )}
                  </HeaderInfo>
                  {isOpen(i.toString()) ? (
                    <plume.glyphs.stroke.CaretUp size={18} />
                  ) : (
                    <plume.glyphs.stroke.CaretDown size={18} />
                  )}
                </HeaderContainer>
                <AccordionWrapper open={isOpen(i.toString())} openOnDesktop={true}>
                  <SponsorContent>
                    <Description>{models.sponsorship.description(sponsorship)}</Description>
                    {sponsorship.channel ? (
                      <plume.ChannelCard
                        avatar={models.user.avatar(sponsorship.sponsor.user, '40x40')}
                        description={t('With the support of %(name)s', { name: sponsorship.sponsor.user.name })}
                        href={sponsorship.channel.absolute_url}
                        src={models.channel.ctaBackgroundImage(sponsorship.channel)}
                        title={models.channel.name(sponsorship.channel)}
                      />
                    ) : (
                      <plume.styles.heading.XXXXXS>
                        <plume.Link href={sponsorship.sponsor.link} kind="secondary">
                          {t('Learn more about %(sponsorName)s', { sponsorName: sponsorship.sponsor.name })}
                        </plume.Link>
                      </plume.styles.heading.XXXXXS>
                    )}
                  </SponsorContent>
                </AccordionWrapper>
              </SponsorContainer>
            )
          })}
        </Styles.SidebarItem>
      )}
    </>
  )
}

const SponsorHeading = styled(Styles.SidebarHeading)`
  margin-bottom: 10px;

  ${plume.styles.heading.M}, ${plume.styles.heading.S} {
    margin-bottom: 10px;
  }

  ${plume.styles.copy.M.light} {
    padding-bottom: 5px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    ${plume.styles.copy.M.light} {
      white-space: nowrap;
    }
  }
`

const SponsorMode = styled(plume.styles.heading.XXXXXS)`
  display: none;
  margin-bottom: 5px;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: block;
  }
`

const SponsorContainer = styled.div`
  margin-top: 30px;

  ${plume.styles.heading.XXXXXS} {
    margin-bottom: 5px;
  }
`

const Avatar = styled.a`
  margin-right: 10px;

  ${plume.styles.image.Avatar} {
    width: 34px;
    height: 34px;
  }
`

const HeaderContainer = styled.div`
  display: flex;

  ${plume.glyphs.Icon} {
    margin-left: auto;
    color: ${plume.COLORS.PRIMARY_SAND_500};
    margin-top: 5px;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.glyphs.Icon} {
      display: none;
    }
  }
`

const TitleContainer = styled.div`
  margin-right: 15px;
  margin-bottom: 4px;
`

const HeaderInfo = styled.div`
  display: flex;
  align-items: end;
  flex-direction: column;

  ${plume.styles.heading.XS} {
    text-decoration: none;
    line-height: 19px;
  }

  ${plume.Tag} {
    flex-shrink: 0;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
`

const SponsorContent = styled.div`
  margin-left: 0px;
  width: 100%;

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    margin-left: 48px;
    width: calc(100% - 48px);
  }
`

const Description = styled(plume.styles.copy.S.regular)`
  margin-top: 10px;
  margin-bottom: 10px;
`
